import { supabase } from '@/integrations/supabase/client';
import { RegistrationFormData, registrationSchema, validateCourseSpecificFields, sanitizeFormData } from './registrationSchema';

// Retry configuration
const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 5000,  // 5 seconds
};

// Error types for better handling
export enum RegistrationErrorType {
  VALIDATION = 'VALIDATION',
  DUPLICATE = 'DUPLICATE', 
  NETWORK = 'NETWORK',
  PERMISSION = 'PERMISSION',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN'
}

export interface RegistrationError extends Error {
  type: RegistrationErrorType;
  code?: string;
  retryable: boolean;
}

// Create custom registration error
const createRegistrationError = (
  message: string, 
  type: RegistrationErrorType, 
  retryable: boolean = false,
  code?: string
): RegistrationError => {
  const error = new Error(message) as RegistrationError;
  error.type = type;
  error.retryable = retryable;
  error.code = code;
  return error;
};

// Retry mechanism with exponential backoff
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  attempt: number = 1
): Promise<T> => {
  try {
    return await fn();
  } catch (error: any) {
    if (attempt >= RETRY_CONFIG.maxAttempts || !error.retryable) {
      throw error;
    }

    const delay = Math.min(
      RETRY_CONFIG.baseDelay * Math.pow(2, attempt - 1),
      RETRY_CONFIG.maxDelay
    );

    console.log(`⏳ Retry attempt ${attempt + 1} in ${delay}ms...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    return retryWithBackoff(fn, attempt + 1);
  }
};

// Network connectivity check — rely on the browser's online flag.
// (A HEAD to /rest/v1/ returns 401 with the new Supabase signing-keys system,
// which incorrectly tripped the offline error before.)
const checkNetworkConnectivity = async (): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    return navigator.onLine;
  }
  return true;
};

// Best-effort logging of blocked spam attempts (fire-and-forget)
let cachedIp: string | null = null;
const getClientIp = async (): Promise<string | null> => {
  if (cachedIp !== null) return cachedIp;
  try {
    const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    cachedIp = data?.ip ?? null;
    return cachedIp;
  } catch {
    return null;
  }
};

const logBlockedAttempt = (
  data: { email?: string; phone?: string; firstName?: string; lastName?: string },
  reason: 'LIMIT_REACHED' | 'TOO_SOON'
): void => {
  // Don't await — never block UX on logging
  (async () => {
    try {
      const ip = await getClientIp();
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
      await supabase.rpc('log_registration_block', {
        p_email: data.email ?? '',
        p_phone: data.phone ?? '',
        p_first_name: data.firstName ?? '',
        p_last_name: data.lastName ?? '',
        p_reason: reason,
        p_ip_address: ip,
        p_user_agent: ua,
      });
    } catch (e) {
      console.warn('Block log failed:', e);
    }
  })();
};

// Enhanced database save with comprehensive error handling
export const saveRegistrationToDatabase = async (formData: RegistrationFormData): Promise<any> => {
  console.log('📝 Starting registration save process...');
  
  // Step 1: Validate input data
  console.log('🔍 Step 1: Validating input data...');
  const validationResult = registrationSchema.safeParse(formData);
  
  if (!validationResult.success) {
    const errorMessages = validationResult.error.errors.map(e => e.message).join(', ');
    throw createRegistrationError(
      `Տվյալների ստուգման սխալ: ${errorMessages}`,
      RegistrationErrorType.VALIDATION,
      false
    );
  }

  // Step 2: Course-specific validation
  console.log('🔍 Step 2: Course-specific validation...');
  const courseErrors = validateCourseSpecificFields(validationResult.data);
  if (courseErrors.length > 0) {
    throw createRegistrationError(
      courseErrors.join(', '),
      RegistrationErrorType.VALIDATION,
      false
    );
  }

  // Step 3: Sanitize data
  console.log('🧹 Step 3: Sanitizing data...');
  const sanitizedData = sanitizeFormData(validationResult.data);

  // Step 4: Check network connectivity
  console.log('🌐 Step 4: Checking network connectivity...');
  const isOnline = await checkNetworkConnectivity();
  if (!isOnline) {
    throw createRegistrationError(
      'Ինտերնետ կապի խնդիր: Ստուգեք կապը և փորձեք կրկին',
      RegistrationErrorType.NETWORK,
      true
    );
  }

  // Step 5: Prepare registration data
  console.log('📋 Step 5: Preparing registration data...');
  const courseDetails = [
    sanitizedData.course,
    sanitizedData.specialization,
    sanitizedData.language,
    sanitizedData.learningType,
    sanitizedData.learningFormat,
    `${sanitizedData.duration} ամիս`
  ].filter(Boolean).join(' - ');

  const registrationData = {
    first_name: sanitizedData.firstName,
    last_name: sanitizedData.lastName,
    email: sanitizedData.email,
    phone: sanitizedData.phone,
    course: courseDetails.trim() || sanitizedData.course.trim() || 'Ծրագրավորման դասընթաց',
    status: 'pending' as const
  };

  console.log('💾 Step 6: Saving to database...', {
    ...registrationData,
    email: sanitizedData.email.slice(0, 3) + '***' // Mask email in logs
  });

  // Step 6: Save with retry logic
  return retryWithBackoff(async () => {
    try {
      const { data: newId, error } = await supabase.rpc('submit_registration', {
        p_first_name: registrationData.first_name,
        p_last_name: registrationData.last_name,
        p_email: registrationData.email,
        p_phone: registrationData.phone,
        p_course: registrationData.course,
      });

      if (error) {
        console.error('❌ Database error:', error);

        // Spam protection: too many submissions
        if (error.message?.includes('LIMIT_REACHED')) {
          logBlockedAttempt(sanitizedData, 'LIMIT_REACHED');
          throw createRegistrationError(
            'Այս էլ․ փոստի կամ հեռախոսահամարով արդեն ուղարկվել է առավելագույն 3 հայտ։ Խնդրում ենք կապվել մեզ հետ ուղղակիորեն։',
            RegistrationErrorType.DUPLICATE,
            false,
            error.code
          );
        }

        // Spam protection: too quick consecutive submissions
        if (error.message?.includes('TOO_SOON')) {
          logBlockedAttempt(sanitizedData, 'TOO_SOON');
          throw createRegistrationError(
            'Խնդրում ենք սպասել մեկ րոպե նոր հայտ ուղարկելուց առաջ։',
            RegistrationErrorType.DUPLICATE,
            false,
            error.code
          );
        }

        // Categorize and handle specific errors
        if (error.code === '23505') {
          throw createRegistrationError(
            'Այս էլ․ փոստի հասցեով արդեն գրանցվել են',
            RegistrationErrorType.DUPLICATE,
            false,
            error.code
          );
        }
        
        
        if (error.message?.includes('row-level security') || error.message?.includes('policy')) {
          throw createRegistrationError(
            'Մուտքի թույլտվության սխալ: Փորձեք մի քանի վայրկյան հետո',
            RegistrationErrorType.PERMISSION,
            true,
            error.code
          );
        }
        
        if (error.message?.includes('violates not-null constraint')) {
          throw createRegistrationError(
            'Պարտադիր դաշտերը բաց են մնացել',
            RegistrationErrorType.VALIDATION,
            false,
            error.code
          );
        }
        
        if (error.message?.includes('timeout') || error.message?.includes('network')) {
          throw createRegistrationError(
            'Ցանցային կապի խնդիր: Փորձեք կրկին',
            RegistrationErrorType.NETWORK,
            true,
            error.code
          );
        }
        
        // Generic server error
        throw createRegistrationError(
          `Սերվերի սխալ: ${error.message}`,
          RegistrationErrorType.SERVER,
          true,
          error.code
        );
      }

      // The security-definer function returns the real registration id,
      // which the confirmation email function needs to verify the record.
      const result = {
        id: newId as unknown as string,
        ...registrationData,
      } as any;

      console.log('✅ Registration saved successfully:', {
        id: result.id,
        email: sanitizedData.email.slice(0, 3) + '***' // Mask email in logs
      });
      
      return result;

    } catch (error: any) {
      // Handle fetch/network errors
      if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        throw createRegistrationError(
          'Ցանցային կապի սխալ: Ստուգեք ինտերնետ կապը',
          RegistrationErrorType.NETWORK,
          true
        );
      }
      
      // Re-throw our custom errors
      if (error.type) {
        throw error;
      }
      
      // Unknown error
      throw createRegistrationError(
        `Անհայտ սխալ: ${error.message}`,
        RegistrationErrorType.UNKNOWN,
        false
      );
    }
  });
};

// Enhanced email sending with retry
export const sendRegistrationConfirmationEmail = async (
  formData: RegistrationFormData, 
  registrationId: string
): Promise<void> => {
  console.log('📧 Sending confirmation email...');
  
  return retryWithBackoff(async () => {
    try {
      const courseDetails = [
        formData.course,
        formData.specialization,
        formData.language,
        formData.learningType,
        formData.learningFormat,
        formData.duration
      ].filter(Boolean).join(' - ');

      const { error } = await supabase.functions.invoke('send-registration-confirmation', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          course: courseDetails || formData.course,
          registrationId: registrationId
        }
      });

      if (error) {
        console.error('❌ Email error:', error);
        
        if (error.message?.includes('timeout') || error.message?.includes('network')) {
          throw createRegistrationError(
            'Էլ․ նամակ ուղարկելու ցանցային սխալ',
            RegistrationErrorType.NETWORK,
            true
          );
        }
        
        throw createRegistrationError(
          `Էլ․ նամակ ուղարկելու սխալ: ${error.message}`,
          RegistrationErrorType.SERVER,
          true
        );
      }

      console.log('✅ Confirmation email sent successfully');
    } catch (error: any) {
      if (error.type) {
        throw error;
      }
      
      throw createRegistrationError(
        `Էլ․ նամակի սխալ: ${error.message}`,
        RegistrationErrorType.SERVER,
        true
      );
    }
  });
};